import { useState } from "react";
import { Stack, Popconfirm } from "@altimateai/lego";
import { Button, toast } from "@altimateai/lego";
import { Connection, CommonIntegrationInstance } from "./mcpSlice";
import { DeleteIcon, EditIcon } from "@assets/icons";
import ConnectionsForm from "./ConnectionsForm";
import { cn } from "@/lib/utils";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteConnection } from "@/helpers/apis";
import { ConnectionsQueryKey } from "./constants";

interface ConnectionItemProps {
  connection: Connection;
  integration: CommonIntegrationInstance;
  onEditSuccess?: () => void;
  onSelect?: (connectionId: string, integrationId: string) => void;
  isSelected?: boolean;
}

const ConnectionItem = ({
  connection,
  integration,
  onEditSuccess,
  onSelect,
  isSelected = false,
}: ConnectionItemProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteConnection(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ConnectionsQueryKey] });
      toast({
        title: "Connection deleted",
        description: "Connection has been deleted successfully.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete connection. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  if (isEditing) {
    return (
      <ConnectionsForm
        integration={integration}
        onSuccess={() => {
          setIsEditing(false);
          onEditSuccess?.();
        }}
        onCancel={() => setIsEditing(false)}
        connection={connection}
      />
    );
  }

  return (
    <div>
      <button
        className={cn(
          "al-p-3 al-border al-rounded-md al-cursor-pointer hover:al-bg-gray-50 al-text-left al-w-full",
          isSelected && "al-border-blue-500 al-bg-blue-50"
        )}
        type="button"
        onClick={() => setShowDetails(prev => !prev)}
      >
        <Stack justify="between" align="start">
          <Stack direction="column" className="al-gap-1">
            <h4 className="al-font-medium al-py-2">{connection.name}</h4>
            {showDetails && (
              <div
                className="al-text-xs al-text-gray-600 al-mt-2 al-space-y-1 al-break-all al-max-w-xl"
                onClick={e => e.stopPropagation()}
                onKeyDown={e => e.stopPropagation()}
                role="presentation"
              >
                {Object.entries(connection.config).length === 0 ? (
                  <div className="al-text-gray-400">No config</div>
                ) : (
                  Object.entries(connection.config).map(([key, value]) => {
                    const configDef = integration.config?.find(c => c.key === key);
                    const label = configDef?.name || key;
                    return (
                      <Stack key={key} direction="column" className="al-gap-2">
                        <span className="al-font-semibold">{label}:</span>
                        <span>
                          {typeof value === "object" ? JSON.stringify(value) : String(value)}
                        </span>
                      </Stack>
                    );
                  })
                )}
              </div>
            )}
          </Stack>
          <Stack direction="row" className="al-gap-1">
            {onSelect && connection.id && integration.id && (
              <Button
                type="button"
                variant={isSelected ? "default" : "outline"}
                size="sm"
                onClick={e => {
                  e.stopPropagation();
                  if (connection.id && integration.id) {
                    onSelect(connection.id, integration.id);
                  }
                }}
                disabled={isSelected}
              >
                {isSelected ? "Selected" : "Select"}
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={e => {
                e.stopPropagation();
                setIsEditing(true);
              }}
            >
              <EditIcon className="al-w-4 al-h-4" />
            </Button>
            <Popconfirm
              title="Are you sure you want to delete this connection?"
              description="This action cannot be undone."
              onCancel={e => {
                e.stopPropagation();
              }}
              onConfirm={e => {
                e.stopPropagation();
                connection.id && handleDelete(connection.id);
              }}
            >
              <Button type="button" variant="ghost" size="sm" onClick={e => e.stopPropagation()}>
                <DeleteIcon className="al-w-4 al-h-4" />
              </Button>
            </Popconfirm>
          </Stack>
        </Stack>
      </button>
    </div>
  );
};

export default ConnectionItem;
